import type { BelegDatei, BelegRow, DbSnapshot, ImportPlan, DateiPlan, MieterFelder, MieterNeu, WegFelder, EigentuemerAktion, KautionFelder } from './types';
import { normName, splitName } from './parse';

function felderAus(row: BelegRow): MieterFelder {
  const f: MieterFelder = {};
  if (row.email) f.email = row.email;
  if (row.telefon) f.telefon = row.telefon;
  if (row.mobil) f.mobil = row.mobil;
  if (row.mietbeginn) f.mietbeginn = row.mietbeginn;
  if (row.mietende) f.mietende = row.mietende;
  if (row.kaltmiete != null) f.kaltmiete = row.kaltmiete;
  if (row.nebenkosten != null) f.nebenkosten_vorauszahlung = row.nebenkosten;
  if (row.heizkosten != null) f.heizkosten = row.heizkosten;
  if (row.kaution != null) f.kaution = row.kaution;
  return f;
}

function neuAus(row: BelegRow): MieterNeu {
  const { vorname, nachname, firma_name } = splitName(row.name);
  const notiz: string[] = [];
  if (row.adresse) notiz.push('Adresse: ' + row.adresse);
  if (row.debitor) notiz.push('Debitor: ' + row.debitor);
  return {
    vorname, nachname, firma_name,
    mietbeginn: row.mietbeginn || '2000-01-01',
    mietende: row.mietende || null,
    email: row.email || null, telefon: row.telefon || null, mobil: row.mobil || null,
    notizen: notiz.join(' | ') || null,
    kaltmiete: row.kaltmiete ?? null,
    nebenkosten_vorauszahlung: row.nebenkosten ?? null,
    heizkosten: row.heizkosten ?? null,
    kaution: row.kaution ?? null,
  };
}

function einheitExtra(row: BelegRow): { wohnflaeche?: number | null; nutzflaeche?: number | null; zimmer?: number | null } {
  const e: { wohnflaeche?: number | null; nutzflaeche?: number | null; zimmer?: number | null } = {};
  if (row.wohnflaeche != null) e.wohnflaeche = row.wohnflaeche;
  if (row.nutzflaeche != null) e.nutzflaeche = row.nutzflaeche;
  if (row.zimmer != null) e.zimmer = row.zimmer;
  return e;
}

function wegAus(row: BelegRow): WegFelder {
  const w: WegFelder = {};
  if (row.mea != null) w.mea = row.mea;
  if (row.hausgeld != null) w.hausgeld_vorschuss = row.hausgeld;
  if (row.ruecklage != null) w.ruecklage_vorschuss = row.ruecklage;
  if (row.sevStatus) w.sev_status = row.sevStatus;
  if (row.mietbeginn) w.vertrag_ab = row.mietbeginn;
  if (row.mietende) w.vertrag_bis = row.mietende;
  if (row.debitor) w.debitor = row.debitor;
  return w;
}

function kautionFelderAus(row: BelegRow): KautionFelder {
  const f: KautionFelder = {};
  if (row.kaution != null) f.kaution = row.kaution;
  if (row.kautionIst != null) f.kaution_ist = row.kautionIst;
  if (row.kautionSaldo != null) f.kaution_saldo = row.kautionSaldo;
  if (row.kautionRaten != null) f.kaution_raten = row.kautionRaten;
  if (row.kautionIban) f.kaution_iban = row.kautionIban;
  if (row.kautionGegenstand) f.kaution_gegenstand = row.kautionGegenstand;
  if (row.kautionArt) f.kaution_art = row.kautionArt;
  if (row.kautionGueltigAb) f.kaution_gueltig_ab = row.kautionGueltigAb;
  if (row.kautionGueltigBis) f.kaution_gueltig_bis = row.kautionGueltigBis;
  if (row.kautionBemerkungen) f.kaution_bemerkungen = row.kautionBemerkungen;
  return f;
}

function mieterName(m: { vorname: string | null; nachname: string | null; firma_name: string | null }): string {
  return (m.firma_name && m.firma_name.trim()) || `${m.vorname || ''} ${m.nachname || ''}`.trim();
}

export function baueImportPlan(dateien: BelegDatei[], snap: DbSnapshot): ImportPlan {
  const objektByNr = new Map(snap.objekte.map((o) => [o.objektnummer.trim(), o]));
  const out: DateiPlan[] = [];

  for (const datei of dateien) {
    const dp: DateiPlan = { dateiname: datei.dateiname, objektnummer: datei.objektnummer, objekt_id: null, einheiten: [], mieter: [], eigentuemer: [], kautionen: [], warnungen: [] };
    if (datei.fehler) { dp.fehler = datei.fehler; out.push(dp); continue; }
    const obj = datei.objektnummer ? objektByNr.get(datei.objektnummer.trim()) : undefined;
    if (!obj) { dp.fehler = `Objekt ${datei.objektnummer ?? '(unbekannt)'} nicht gefunden`; out.push(dp); continue; }
    dp.objekt_id = obj.id; dp.objektBezeichnung = obj.bezeichnung;

    const objEinheiten = snap.einheiten.filter((e) => e.objekt_id === obj.id);
    const einheitByBez = new Map(objEinheiten.map((e) => [e.bezeichnung, e]));
    const objMieter = snap.mieter.filter((m) => m.objekt_id === obj.id);
    const usedBez = new Set(objEinheiten.map((e) => e.bezeichnung));
    const objEig = snap.eigentuemer.filter((e) => e.objekt_id === obj.id);
    const objEinheitIds = new Set(objEinheiten.map((e) => e.id));
    const objEinhEig = snap.einheitEigentuemer.filter((l) => objEinheitIds.has(l.einheit_id));

    for (const row of datei.rows) {
      // --- Kaution (nur Kautionen; legt keine Einheiten an) ---
      if (datei.quelle === 'kaution') {
        if (!row.name) continue;
        const tNorm = normName(row.name);
        let treffer = objMieter.filter((m) => normName(mieterName(m)) === tNorm);
        if (treffer.length > 1) {
          const perEinheit = treffer.filter((m) => {
            const e = objEinheiten.find((x) => x.id === m.einheit_id);
            return e && e.bezeichnung === row.einheitBezeichnung;
          });
          if (perEinheit.length === 1) treffer = perEinheit;
        }
        if (treffer.length === 1) {
          dp.kautionen.push({ aktion: 'aktualisieren', objekt_id: obj.id, anzeigeName: row.name, einheitBezeichnung: row.einheitBezeichnung, mieterId: treffer[0].id, felder: kautionFelderAus(row) });
        } else {
          dp.kautionen.push({ aktion: 'uebersprungen', objekt_id: obj.id, anzeigeName: row.name, einheitBezeichnung: row.einheitBezeichnung, grund: treffer.length === 0 ? `Kein Mieter „${row.name}" im Objekt` : `Mieter „${row.name}" mehrdeutig` });
        }
        continue;
      }

      // --- Einheit ---
      const existE = einheitByBez.get(row.einheitBezeichnung);
      const notizen = row.lage ? `Lage: ${row.lage}` : null;
      let bez = row.einheitBezeichnung;
      if (existE) {
        dp.einheiten.push({ aktion: 'aktualisieren', objekt_id: obj.id, einheitId: existE.id, bezeichnung: bez, typ: row.art, notizen, ...einheitExtra(row) });
      } else {
        let n = 2;
        while (usedBez.has(bez)) { bez = `${row.einheitBezeichnung} (${n++})`; }
        usedBez.add(bez);
        dp.einheiten.push({ aktion: 'neu', objekt_id: obj.id, bezeichnung: bez, typ: row.art, notizen, ...einheitExtra(row) });
      }

      // --- Eigentümer (nur Eigentümerverträge) ---
      if (datei.quelle === 'eigentuemer') {
        if (!row.name) continue;
        const tNorm = normName(row.name);
        const match = objEig.find((e) => normName(mieterName(e)) === tNorm);
        const link = existE ? objEinhEig.find((l) => l.einheit_id === existE.id) : undefined;
        let aktion: 'neu' | 'aktualisieren' | 'wechsel' = 'neu';
        let alterName: string | undefined;
        if (link) {
          if (match && link.eigentuemer_id === match.id) {
            aktion = 'aktualisieren';
          } else {
            aktion = 'wechsel';
            const alt = objEig.find((e) => e.id === link.eigentuemer_id);
            alterName = alt ? mieterName(alt) : undefined;
          }
        }
        dp.eigentuemer.push({
          aktion, objekt_id: obj.id, einheitBezeichnung: bez, anzeigeName: row.name,
          eigentuemerId: match?.id,
          neu: match ? undefined : splitName(row.name),
          alterEigentuemerName: alterName,
          weg: wegAus(row),
        });
        continue;
      }

      // --- Mieter (einheit-zentriert) ---
      if (!row.name) continue;
      const tNorm = normName(row.name);
      const aktuell = existE ? objMieter.find((m) => m.einheit_id === existE.id) : undefined;
      if (aktuell) {
        if (normName(mieterName(aktuell)) === tNorm) {
          dp.mieter.push({ aktion: 'aktualisieren', objekt_id: obj.id, einheitBezeichnung: bez, anzeigeName: row.name, mieterId: aktuell.id, felder: felderAus(row) });
        } else {
          dp.mieter.push({ aktion: 'wechsel', objekt_id: obj.id, einheitBezeichnung: bez, anzeigeName: row.name, alterMieterId: aktuell.id, alterMieterName: mieterName(aktuell), neu: neuAus(row) });
        }
      } else {
        const treffer = objMieter.filter((m) => m.einheit_id == null && normName(mieterName(m)) === tNorm);
        if (treffer.length === 1) {
          dp.mieter.push({ aktion: 'aktualisieren', objekt_id: obj.id, einheitBezeichnung: bez, anzeigeName: row.name, mieterId: treffer[0].id, felder: felderAus(row) });
        } else if (treffer.length > 1) {
          dp.mieter.push({ aktion: 'uebersprungen', objekt_id: obj.id, einheitBezeichnung: bez, anzeigeName: row.name, grund: `Name „${row.name}" mehrfach im Objekt` });
        } else {
          dp.mieter.push({ aktion: 'neu', objekt_id: obj.id, einheitBezeichnung: bez, anzeigeName: row.name, neu: neuAus(row) });
        }
      }
    }
    out.push(dp);
  }
  return { dateien: out };
}
